import { User } from "firebase/auth";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  logUserActivity,
  UserActivityEventType,
} from "../services/activityLogger";
import {
  getUserById,
  updateUserOnboardingStatus,
} from "../services/userService";
import { debugLogError } from "../utils/debug-log-error";
import { useUserProfile } from "./useUserProfile";

interface OnboardingStatusHook {
  onboardingCompleted: boolean | null;
  loadingOnboardingStatus: boolean;
  updateOnboardingStatus: (status: boolean) => Promise<void>;
  handleFinishOnboarding: () => Promise<void>;
  handleSkipOnboarding: () => Promise<void>;
}

const useOnboardingStatus = (
  firebaseUser: User | null
): OnboardingStatusHook => {
  const [initialized, setInitialized] = useState(false);
  const { profile, loading: loadingProfile } = useUserProfile(
    firebaseUser?.uid
  );

  const [onboardingCompleted, setOnboardingCompleted] = useState<
    boolean | null
  >(null);
  const [loadingOnboardingStatus, setLoadingOnboardingStatus] =
    useState<boolean>(true);

  useEffect(() => {
    const fetchStatus = async () => {
      if (loadingProfile) {
        return;
      }

      if (firebaseUser) {
        setLoadingOnboardingStatus(true);
        setInitialized(true);

        try {
          const user = await getUserById(firebaseUser.uid);
          if (user === null) {
            throw new Error("User document does not exist");
          }

          const onboardingStatus =
            !!user.onboardingCompleted && !!profile?.username;
          setOnboardingCompleted(onboardingStatus);
        } catch (error) {
          debugLogError("Error fetching onboarding status:", error);
          setOnboardingCompleted(false);
        } finally {
          setLoadingOnboardingStatus(false);
        }
      } else {
        setOnboardingCompleted(null);
        setLoadingOnboardingStatus(false);
        setInitialized(true);
      }
    };

    fetchStatus();
  }, [firebaseUser, profile, loadingProfile]);

  const updateOnboardingStatus = useCallback(
    async (status: boolean) => {
      if (firebaseUser) {
        setLoadingOnboardingStatus(true);
        try {
          await updateUserOnboardingStatus(firebaseUser.uid, status);
          setOnboardingCompleted(status);
        } catch (error) {
          debugLogError("Error updating onboarding status:", error);
        } finally {
          setLoadingOnboardingStatus(false);
        }
      }
    },
    [firebaseUser]
  );

  const handleFinishOnboarding = useCallback(async () => {
    if (firebaseUser) {
      logUserActivity({
        userId: firebaseUser.uid,
        eventType: UserActivityEventType.ONBOARDING_COMPLETED,
        timestamp: Date.now(),
        context: {
          action: "Finished all onboarding steps",
        },
      });
    }
    await updateOnboardingStatus(true);
  }, [firebaseUser, updateOnboardingStatus]);

  const handleSkipOnboarding = useCallback(async () => {
    if (firebaseUser) {
      logUserActivity({
        userId: firebaseUser.uid,
        eventType: UserActivityEventType.ONBOARDING_SKIPPED,
        timestamp: Date.now(),
        context: {
          action: "Skipped onboarding tutorial",
        },
      });
    }

    await updateOnboardingStatus(true);
  }, [firebaseUser, updateOnboardingStatus]);

  return useMemo(
    () => ({
      onboardingCompleted,
      loadingOnboardingStatus: loadingOnboardingStatus || !initialized,
      updateOnboardingStatus,
      handleFinishOnboarding,
      handleSkipOnboarding,
    }),
    [
      onboardingCompleted,
      loadingOnboardingStatus,
      initialized,
      updateOnboardingStatus,
      handleFinishOnboarding,
      handleSkipOnboarding,
    ]
  );
};

export default useOnboardingStatus;
